// Pure display/validation helpers for the model currency admin page.

import { ApiError } from "@/services/adminProgramNews";
import type {
  CandidateStatus,
  EffectiveSource,
  EvalCheck,
  GoldenDimension,
  ModelCandidate,
  ModelCurrencyOverview,
  ModelPrice,
} from "@/services/adminModelCurrency";
import { formatDateTime } from "../programNews/formatters";

export const READ_ONLY_NOTE = "Changes are made in QA and pushed to staging and production";

const SOURCE_LABELS: Record<EffectiveSource, string> = {
  assignment: "Chosen by an admin",
  env: "Environment setting",
  default: "Built-in default",
};

export function sourceLabel(source: EffectiveSource): string {
  return SOURCE_LABELS[source] ?? source;
}

const STATUS_LABELS: Record<CandidateStatus, string> = {
  detected: "Detected",
  evaluating: "Being tested",
  passed: "Passed",
  failed: "Failed",
  dismissed: "Dismissed",
  promoted: "Promoted",
  superseded: "Replaced by a newer model",
};

export function statusLabel(status: CandidateStatus): string {
  return STATUS_LABELS[status] ?? status;
}

export type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

export function statusVariant(status: CandidateStatus): BadgeVariant {
  switch (status) {
    case "passed":
    case "promoted":
      return "default";
    case "failed":
      return "destructive";
    case "detected":
    case "evaluating":
      return "secondary";
    default:
      return "outline";
  }
}

/** A candidate can be promoted only once its automatic test has passed. */
export function canPromote(candidate: ModelCandidate): boolean {
  return candidate.status === "passed" && candidate.evalResults?.passed === true;
}

/** Candidates that still need attention: not failed, dismissed or already promoted. */
const WAITING: CandidateStatus[] = ["detected", "evaluating", "passed"];

export function countWaitingCandidates(overview: ModelCurrencyOverview): number {
  return overview.useCases.reduce(
    (total, useCase) => total + useCase.candidates.filter(c => WAITING.includes(c.status)).length,
    0
  );
}

/** Candidates the Run test button applies to. */
const RUNNABLE: CandidateStatus[] = ["detected", "failed"];

export function canRunTest(candidate: ModelCandidate, writable: boolean): boolean {
  return writable && candidate.canRunHere && RUNNABLE.includes(candidate.status);
}

/** Closed or finished candidates never need a "where to run it" hint. */
const NO_HINT: CandidateStatus[] = ["passed", "promoted", "dismissed"];

export function shouldShowRunHint(candidate: ModelCandidate): boolean {
  return !candidate.canRunHere && !!candidate.runHint && !NO_HINT.includes(candidate.status);
}

function allCandidates(overview: ModelCurrencyOverview): ModelCandidate[] {
  return overview.useCases.flatMap(useCase => useCase.candidates);
}

/** Candidates waiting for a test (new or failed) that this server can run: what "Test all" starts. */
export function countRunnableWaiting(overview: ModelCurrencyOverview): number {
  return allCandidates(overview).filter(c => c.canRunHere && RUNNABLE.includes(c.status)).length;
}

export function hasEvaluating(overview: ModelCurrencyOverview | null): boolean {
  return !!overview && allCandidates(overview).some(c => c.status === "evaluating");
}

/** "2 min ago", or null when the start time is missing or unreadable. */
export function startedAgoText(iso: string | null, now: number = Date.now()): string | null {
  if (!iso) return null;
  const started = Date.parse(iso);
  if (Number.isNaN(started)) return null;
  const minutes = Math.floor(Math.max(0, now - started) / 60_000);
  return minutes < 1 ? "just now" : `${minutes} min ago`;
}

function formatValue(value: number | string): string {
  return typeof value === "number" ? String(Math.round(value * 1000) / 1000) : value;
}

/** "0.123 vs limit 0.5" */
export function formatCheck(check: EvalCheck): string {
  const hasValue = check.value !== null && check.value !== undefined;
  const hasLimit = check.limit !== null && check.limit !== undefined;
  if (!hasValue) return "no value";
  if (!hasLimit) return formatValue(check.value as number | string);
  return `${formatValue(check.value as number | string)} vs limit ${formatValue(check.limit as number | string)}`;
}

/** Failing checks in plain words, comma separated. Empty string when none failed. */
export function failingChecksText(checks: EvalCheck[]): string {
  return checks
    .filter(c => !c.passed)
    .map(c => `${c.name} (${formatCheck(c)})`)
    .join(", ");
}

export function formatPercent(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

export function formatDecimal(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return value.toFixed(2);
}

/** Turn an API failure into a sentence a product person can act on. */
export function describeApiError(err: unknown): string {
  if (err instanceof DOMException && (err.name === "TimeoutError" || err.name === "AbortError")) {
    return "The API took too long to answer. It may be down or busy; try again in a moment.";
  }
  if (err instanceof TypeError) {
    return "Can't reach the API. If you're running locally, the local API may be down: start it and try again.";
  }
  if (err instanceof ApiError) {
    if (err.status === 403 && err.data?.error === "registry_not_writable") return READ_ONLY_NOTE;
    if (err.status === 409 && err.data?.error === "not_promotable") {
      const reason = typeof err.data.reason === "string" ? err.data.reason : "it is not ready";
      return `Can't promote this one: ${reason}`;
    }
    if (err.status === 409 && err.data?.error === "not_runnable_here") {
      return typeof err.data.reason === "string"
        ? err.data.reason
        : "This test can't be run from here.";
    }
    if (err.status === 409 && err.data?.error === "eval_in_progress") {
      return "A test is already running. Wait for it to finish.";
    }
    if (err.status === 400 && err.data?.error === "invalid_candidate_id") {
      return "That model isn't valid any more. Refresh the page and try again.";
    }
    if (err.status === 404) return "That item no longer exists. Refresh the page and try again.";
    if (err.status === 401) return "You're signed out. Sign in again, then retry.";
    if (err.status === 403) return "You don't have access to this page.";
    if (err.status >= 500) return "The server hit a problem. Try again in a moment.";
  }
  return err instanceof Error ? err.message : String(err);
}

/** Labels of the dimensions that have no score yet. */
export function missingDimensions(
  dimensions: GoldenDimension[],
  scores: Record<string, number>
): string[] {
  return dimensions.filter(d => typeof scores[d.key] !== "number").map(d => d.label);
}

export function goldenPercent(labeled: number, target: number): number {
  if (!target || target <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((labeled / target) * 100)));
}

export function goldenLabel(labeled: number, target: number): string {
  return `${labeled} of ${target} labeled`;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function summaryLine(overview: ModelCurrencyOverview): string {
  const tracked = plural(overview.useCases.length, "model tracked", "models tracked");
  const waiting = plural(
    countWaitingCandidates(overview),
    "candidate waiting",
    "candidates waiting"
  );
  const last = overview.checkState.lastRunAt
    ? formatDateTime(overview.checkState.lastRunAt)
    : "never";
  const { labeled, target } = overview.goldenSet;
  return `${tracked}, ${waiting}, last check ${last}, golden set ${labeled}/${target}`;
}

/** Plain-text summary only; structured summaries (objects) are shown through the checks instead. */
export function summaryText(summary: unknown): string | null {
  return typeof summary === "string" && summary.length > 0 ? summary : null;
}

// ---- Prices ----

export const NO_PRICE_TEXT = "Price not in our table yet";
export const NO_PRICE_WARNING = `${NO_PRICE_TEXT}. Add it before promoting so cost estimates are right.`;

/** $30, $2.50, $0.15, $0.075: whole dollars bare, otherwise at least two decimals. */
export function formatMoney(value: number): string {
  if (Number.isInteger(value)) return `$${value}`;
  const twoDecimals = value.toFixed(2);
  if (Math.abs(Number(twoDecimals) - value) < 1e-9) return `$${twoDecimals}`;
  return `$${Number(value.toFixed(3))}`;
}

export function formatPrice(price: ModelPrice | null | undefined): string {
  if (!price) return NO_PRICE_TEXT;
  return `Costs ${formatMoney(price.input)} in / ${formatMoney(price.output)} out per million tokens`;
}

/** How a candidate's price compares to the current model's; null when unknown. */
export function priceComparison(ratio: number | null | undefined): string | null {
  if (typeof ratio !== "number" || !Number.isFinite(ratio)) return null;
  if (ratio >= 0.9 && ratio <= 1.1) return "About the same price";
  const text = `About ${ratio.toFixed(1)}× the current price`;
  return ratio < 0.9 ? `${text} (cheaper)` : text;
}

export function priceFootnote(asOf: string, source?: string): string {
  if (source === "fetched") {
    return `Prices were pulled from OpenAI's and Anthropic's pricing pages on ${asOf}. "In" is what we send; "out" is what the model writes back. The comparison is a rough average of the two.`;
  }
  return `Prices are OpenAI list prices per million tokens as of ${asOf}, kept up to date by hand. "In" is what we send; "out" is what the model writes back. The comparison is a rough average of the two.`;
}
